import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { MenuBulkJobRow } from '@/lib/api/menu';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Circle,
  Download,
  Loader2,
  RotateCw,
  XCircle,
} from 'lucide-react';

type BulkJobHistoryProps = {
  jobs: MenuBulkJobRow[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: string | null;
  onRetry: () => void;
  activeStatuses: Set<MenuBulkJobRow['status']>;
};

const JOB_TYPE_LABELS: Record<MenuBulkJobRow['jobType'], string> = {
  import_csv: 'CSV import',
  import_json: 'JSON import',
  export_csv: 'CSV export',
  export_json: 'JSON export',
  rollback: 'Rollback',
};

const STATUS_CONFIG: Record<
  MenuBulkJobRow['status'],
  { label: string; badgeClass: string; icon: React.ComponentType<{ className?: string }> }
> = {
  pending: {
    label: 'Pending',
    badgeClass: 'bg-muted text-muted-foreground border-border',
    icon: Loader2,
  },
  validating: {
    label: 'Validating',
    badgeClass: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    icon: Loader2,
  },
  running: {
    label: 'In progress',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    icon: Loader2,
  },
  completed: {
    label: 'Completed',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    icon: CheckCircle2,
  },
  failed: {
    label: 'Failed',
    badgeClass: 'bg-red-500/10 text-red-600 border-red-500/30',
    icon: XCircle,
  },
  cancelled: {
    label: 'Cancelled',
    badgeClass: 'bg-muted text-muted-foreground border-border',
    icon: Circle,
  },
  rolled_back: {
    label: 'Rolled back',
    badgeClass: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    icon: RotateCw,
  },
};

const formatTimestamp = (value: number | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const formatProgress = (job: MenuBulkJobRow): number => {
  if (Number.isFinite(job.progressPercent) && job.progressPercent >= 0) {
    return Math.min(100, Math.max(0, job.progressPercent));
  }
  if (job.status === 'completed') return 100;
  return 0;
};

const getSummaryFromMetadata = (job: MenuBulkJobRow) => {
  const summary = (job.metadata?.summary ?? {}) as Partial<{
    categoriesInserted: number;
    itemsInserted: number;
    modifierGroupsInserted: number;
    modifiersInserted: number;
  }>;
  return summary;
};

export function BulkJobHistory({
  jobs,
  isLoading = false,
  isRefreshing = false,
  error,
  onRetry,
  activeStatuses,
}: BulkJobHistoryProps): JSX.Element {
  const [selectedJob, setSelectedJob] = useState<MenuBulkJobRow | null>(null);

  const sortedJobs = useMemo(
    () => [...jobs].sort((left, right) => right.createdAt - left.createdAt),
    [jobs],
  );

  const noJobs = !isLoading && sortedJobs.length === 0;

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Bulk history</CardTitle>
          <p className="text-sm text-muted-foreground">
            Track recent imports and exports. Active jobs refresh every few seconds.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onRetry} disabled={isLoading || isRefreshing}>
          {isRefreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to load job history</AlertTitle>
            <AlertDescription>
              Try refreshing. If the issue persists, contact support.
            </AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-12 animate-pulse rounded-md bg-muted/40" />
            <div className="h-12 animate-pulse rounded-md bg-muted/40" />
            <div className="h-12 animate-pulse rounded-md bg-muted/40" />
          </div>
        ) : noJobs ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/40 bg-muted/20 px-8 py-12 text-center">
            <CalendarClock className="mb-4 h-10 w-10 text-muted-foreground/60" />
            <p className="text-sm text-muted-foreground">
              No bulk jobs yet. Run an import or export to see progress here.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-96">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border/60 text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Job</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Source</th>
                  <th className="px-3 py-2 text-left font-medium">Progress</th>
                  <th className="px-3 py-2 text-left font-medium">Started</th>
                  <th className="px-3 py-2 text-left font-medium">Updated</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((job) => {
                  const statusConfig = STATUS_CONFIG[job.status];
                  const jobTypeLabel = JOB_TYPE_LABELS[job.jobType] ?? job.jobType;
                  const isActive = activeStatuses.has(job.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <tr
                      key={job.id}
                      className={cn(
                        'border-b border-border/40 transition-colors hover:bg-muted/30',
                        isActive && 'bg-emerald-500/5',
                      )}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {job.id.slice(0, 10)}…
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{jobTypeLabel}</span>
                          <span className="text-xs text-muted-foreground">
                            {job.sourceFormat ? job.sourceFormat.toUpperCase() : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {job.sourceFilename ?? '-'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Progress value={formatProgress(job)} className="h-2 w-24" />
                          <span className="text-xs text-muted-foreground">
                            {formatProgress(job).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {formatTimestamp(job.startedAt ?? job.createdAt)}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {formatTimestamp(job.updatedAt)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={cn('flex items-center gap-1 border', statusConfig.badgeClass)}>
                          <StatusIcon
                            className={cn(
                              'h-3.5 w-3.5',
                              job.status === 'running' || job.status === 'validating' || job.status === 'pending'
                                ? 'animate-spin'
                                : '',
                            )}
                          />
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedJob(job)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </CardContent>

      <Dialog open={Boolean(selectedJob)} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Job detail</DialogTitle>
          </DialogHeader>
          {selectedJob ? (
            <div className="space-y-4">
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">Job ID</span>
                  <span className="font-mono">{selectedJob.id}</span>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <InfoTile label="Type" value={JOB_TYPE_LABELS[selectedJob.jobType] ?? selectedJob.jobType} />
                <InfoTile label="Format" value={selectedJob.sourceFormat?.toUpperCase() ?? '-'} />
                <InfoTile label="Source file" value={selectedJob.sourceFilename ?? '-'} />
                <InfoTile label="Requested by" value={selectedJob.requestedBy ?? '-'} />
                <InfoTile label="Created" value={formatTimestamp(selectedJob.createdAt)} />
                <InfoTile label="Completed" value={formatTimestamp(selectedJob.completedAt)} />
              </div>
              <div className="rounded-md border border-muted/60 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Summary</p>
                  <Badge variant="outline">{formatProgress(selectedJob).toFixed(0)}%</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {Object.entries(getSummaryFromMetadata(selectedJob)).map(([key, value]) => (
                    <div key={key}>
                      <p className="font-medium text-foreground">{key.replace(/([A-Z])/g, ' $1')}</p>
                      <p>{formatNumber(typeof value === 'number' ? value : null)}</p>
                    </div>
                  ))}
                  <div>
                    <p className="font-medium text-foreground">Records processed</p>
                    <p>{formatNumber(selectedJob.processedRecords)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Total records</p>
                    <p>{formatNumber(selectedJob.totalRecords)}</p>
                  </div>
                </div>
              </div>
              {selectedJob.errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription className="text-xs">{selectedJob.errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              {selectedJob.validationSummary ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Validation summary</p>
                  <pre className="max-h-48 overflow-auto rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                    {JSON.stringify(selectedJob.validationSummary, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedJob(null)}>
              Close
            </Button>
            {selectedJob?.metadata?.rejectionFileUrl ? (
              <Button asChild>
                <a href={String(selectedJob.metadata.rejectionFileUrl)} download>
                  <Download className="mr-2 h-4 w-4" />
                  Download errors
                </a>
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

type InfoTileProps = {
  label: string;
  value: string;
};

const InfoTile = ({ label, value }: InfoTileProps) => (
  <div className="rounded-md border border-border/60 bg-muted/20 p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground">{value}</p>
  </div>
);
